import { fixImageUrl, fixProductImages } from "../utils/patchImages";
import { API_CONFIG, API_URLS } from "../config/environment";

// ✅ ACTUALIZADO: Usar configuración centralizada
export const getApiBaseUrl = () => {
  return API_CONFIG.BIENES_URL;
};

export const API_BASE_URL = getApiBaseUrl();

console.log(
  `🌐 API Base URL configured for ${API_CONFIG.version}:`,
  API_BASE_URL
);

const defaultHeaders = {
  "Content-Type": "application/json",
  Accept: "application/json",
  "User-Agent": "TattersallVentaUsados/2.0",
};

// Función para crear headers autenticados
const createAuthHeaders = (token = null, additionalHeaders = {}) => {
  const headers = { ...defaultHeaders, ...additionalHeaders };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const handleResponse = async (response) => {
  // Verificar si el token expiró
  if (response.status === 401 || response.status === 403) {
    console.warn("🔐 Token expirado o no autorizado en API");
    // Emitir evento para que AuthContext maneje el logout
    window.dispatchEvent(
      new CustomEvent("token-expired", {
        detail: { status: response.status },
      })
    );

    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message ||
        errorData.error?.message ||
        "Token expirado o no autorizado"
    );
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    if (errorData.success === false && errorData.error) {
      throw new Error(
        errorData.error.message || `HTTP error! status: ${response.status}`
      );
    }

    throw new Error(
      errorData.message || `HTTP error! status: ${response.status}`
    );
  }
  return response.json();
};

const buildQueryParams = (params) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      if (Array.isArray(value)) {
        value.forEach((item) => searchParams.append(key, item));
      } else {
        searchParams.append(key, value.toString());
      }
    }
  });

  return searchParams.toString();
};

export const productsService = {
  // ✅ ACTUALIZADO: Obtener productos con soporte para autenticación
  getProducts: async (params = {}, token = null) => {
    try {
      const defaultParams = {
        page: 1,
        limit: 10,
        featured: false,
        area: 1, // ✅ NUEVO: Filtrar por área "Bienes" por defecto
        ...params,
      };

      const {
        page,
        limit,
        search = "",
        category = "",
        brand = "", // ✅ NUEVO: Parámetro para marca
        model = "", // ✅ CORREGIDO: Usar "model" directamente
        minPrice = "",
        maxPrice = "",
        minYear = "",
        maxYear = "",
        estado = "", // ✅ CORREGIDO: Usar estado en lugar de condition (valores: "operativo", "no operativo", null)
        location = "",
        features = [],
        sortBy = "", // ✅ ACTUALIZADO: usar createdAt según backend
        sortOrder = "desc",
        featured,
        area,
      } = defaultParams;

      const queryParams = buildQueryParams({
        page,
        limit,
        search,
        category,
        brand, // ✅ NUEVO: Agregar brand a los parámetros
        model, // ✅ CORREGIDO: Usar model directamente
        priceMin: minPrice,
        priceMax: maxPrice,
        yearMin: minYear,
        yearMax: maxYear,
        estado, // ✅ CORREGIDO: Usar estado en lugar de condition
        location,
        features,
        sortBy,
        sortOrder,
        featured,
        area,
      });

      const url = `${API_BASE_URL}/products?${queryParams}`;
      console.log(`🌐 Fetching products from ${API_CONFIG.version} API:`, url);

      const response = await fetch(url, {
        method: "GET",
        headers: createAuthHeaders(token),
      });

      const responseData = await handleResponse(response);

      console.log(`🔍 RAW API Response (${API_CONFIG.version}):`, responseData);

      if (responseData.error) {
        throw new Error(
          responseData.error?.message || "API returned success: false"
        );
      }

      const productsArray = responseData.data || [];
      const pagination = responseData.pagination || {};
      const meta = responseData.meta || {};

      // 🩹 PARCHE TEMPORAL: Corregir URLs de imágenes si es necesario
      productsArray.forEach((product) => {
        if (product.images && Array.isArray(product.images)) {
          product.images = fixProductImages(product.images);
        } else if (product.image) {
          product.image = fixImageUrl(product.image);
        }
      });

      console.log(
        `✅ Products fetched successfully from ${API_CONFIG.version}:`,
        {
          total: pagination.total,
          page: pagination.page,
          totalPages: pagination.totalPages,
          productsCount: productsArray.length,
          hasNextPage: pagination.hasNextPage,
          appliedFilters: meta.appliedFilters,
          processingTime: meta.processingTime,
        }
      );

      return {
        products: productsArray,
        page: pagination.page || 1,
        limit: pagination.limit || limit,
        total: pagination.total || 0,
        totalPages: pagination.totalPages || 1,
        hasNextPage: pagination.hasNextPage || false,
        hasPrevPage: pagination.hasPrevPage || false,
        from: pagination.from || 0,
        to: pagination.to || 0,
      };
    } catch (error) {
      console.error(
        `❌ Error fetching products from ${API_CONFIG.version}:`,
        error
      );
      throw error;
    }
  },

  // ✅ ACTUALIZADO: Obtener un producto específico por ID con autenticación
  getProductById: async (id, token = null) => {
    try {
      const response = await fetch(`${API_BASE_URL}/products/${id}`, {
        method: "GET",
        headers: createAuthHeaders(token),
      });

      const responseData = await handleResponse(response);

      // ✅ V2.1: Verificar campo success
      if (responseData.error) {
        throw new Error(responseData.error?.message || "Product not found");
      }

      const product = responseData.data;
      console.log(
        `✅ Product fetched successfully from ${API_CONFIG.version}:`,
        product
      );
      return product;
    } catch (error) {
      console.error(
        `❌ Error fetching product from ${API_CONFIG.version}:`,
        error
      );
      throw error;
    }
  },

  getProductBySlug: async (slug, token = null) => {
    try {
      const response = await fetch(`${API_BASE_URL}/products/slug/${slug}`, {
        method: "GET",
        headers: createAuthHeaders(token),
      });

      const responseData = await handleResponse(response);

      if (responseData.error) {
        throw new Error(responseData.error?.message || "Product not found");
      }

      const product = responseData.data;
      console.log(
        `✅ Product by slug fetched successfully from ${API_CONFIG.version}:`,
        product
      );
      return product;
    } catch (error) {
      console.error(
        `❌ Error fetching product by slug from ${API_CONFIG.version}:`,
        error
      );
      throw error;
    }
  },

  getFilterOptions: async (token = null) => {
    try {
      console.log(
        `🔧 Fetching filter options from ${API_CONFIG.version} dedicated endpoint...`
      );

      const response = await fetch(`${API_BASE_URL}/products/filters`, {
        method: "GET",
        headers: createAuthHeaders(token),
      });

      const responseData = await handleResponse(response);
      console.log(
        `🔍 Raw filter data from ${API_CONFIG.version} backend:`,
        responseData
      );

      if (responseData.error) {
        throw new Error(
          responseData.error?.message || "Failed to fetch filter options"
        );
      }

      const filterOptions = responseData?.filterOptions || {};

      console.log(`✅ Filter options processed from ${API_CONFIG.version}:`, {
        categories: filterOptions.categories?.length || 0,
        brands: filterOptions.brands?.length || 0,
        locations: filterOptions.locations?.length || 0,
        features: filterOptions.features?.length || 0,
        priceRange: filterOptions.priceRange,
        yearRange: filterOptions.yearRange,
      });

      return {
        filterOptions: {
          categories: filterOptions.categories || [],
          brands: filterOptions.brands || [],
          models: filterOptions.models || [], // ✅ CORREGIDO: usar filterOptions.models
          locations: filterOptions.locations || [],
          features: filterOptions.features || [],
          priceRange: filterOptions.priceRange || { min: 0, max: 500000000 },
          yearRange: filterOptions.yearRange || {
            min: 2000,
            max: new Date().getFullYear(),
          },
        },
      };
    } catch (error) {
      console.error(
        `❌ Error fetching filter options from ${API_CONFIG.version}:`,
        error
      );

      // ✅ FALLBACK: Opciones por defecto en caso de error
      return {
        filterOptions: {
          categories: [],
          brands: [],
          models: [],
          locations: [],
          features: [],
          priceRange: { min: 0, max: 500000000 },
          yearRange: { min: 2000, max: new Date().getFullYear() },
        },
      };
    }
  },

  // ✅ NUEVO: Obtener sugerencias de búsqueda para autocompletado
  getSearchSuggestions: async (query, params = {}, token = null) => {
    try {
      const defaultParams = {
        limit: 10,
        area: 1, // Filtrar por área "Bienes" por defecto
        ...params,
      };

      const queryParams = buildQueryParams({
        q: query.trim(),
        limit: defaultParams.limit,
        area: defaultParams.area,
      });

      const url = `${API_BASE_URL}/products/search-suggestions?${queryParams}`;
      console.log(
        `🔍 Fetching search suggestions from ${API_CONFIG.version}:`,
        url
      );

      const response = await fetch(url, {
        method: "GET",
        headers: createAuthHeaders(token),
      });

      const responseData = await handleResponse(response);

      if (responseData.error) {
        throw new Error(
          responseData.error?.message || "Error fetching search suggestions"
        );
      }

      const suggestions = responseData.suggestions || [];

      console.log(`✅ Search suggestions fetched from ${API_CONFIG.version}:`, {
        query,
        suggestionsCount: suggestions.length,
        types: [...new Set(suggestions.map((s) => s.type))],
      });

      return {
        suggestions: suggestions.map((suggestion) => ({
          ...suggestion,
          // Asegurar que todas las sugerencias tengan la estructura esperada
          type: suggestion.type || "product",
          value: suggestion.value || "",
          count: suggestion.count || 0,
          category: suggestion.category || null,
        })),
        query: query.trim(),
        total: suggestions.length,
      };
    } catch (error) {
      console.error(
        `❌ Error fetching search suggestions from ${API_CONFIG.version}:`,
        error
      );

      // ✅ FALLBACK: Si el endpoint no existe, generar sugerencias desde filtros existentes
      console.log("🔄 Using fallback suggestions from filter options...");
      return {
        suggestions: [],
        query: query.trim(),
        total: 0,
        fallback: true,
      };
    }
  },

  // ✅ CORREGIDO: Obtener categorías populares con autenticación
  getPopularCategories: async (params = {}, token = null) => {
    try {
      const queryParams = new URLSearchParams({
        limit: params.limit || 6,
        area: params.area || 1,
        ...params,
      });

      console.log(
        `🔍 Fetching popular categories with params (${API_CONFIG.version}):`,
        queryParams.toString()
      );

      const response = await fetch(
        `${API_BASE_URL}${API_CONFIG.ENDPOINTS.POPULAR_CATEGORIES}?${queryParams}`,
        {
          method: "GET",
          headers: createAuthHeaders(token),
        }
      );

      const responseData = await handleResponse(response);

      if (responseData.error) {
        throw new Error(
          responseData.error?.message || "Error fetching popular categories"
        );
      }

      console.log(
        `✅ Popular categories API response (${API_CONFIG.version}):`,
        {
          categories: responseData.data?.length || 0,
          total: responseData.meta?.totalCategories || 0,
        }
      );

      return responseData;
    } catch (error) {
      console.error(
        `❌ Error in getPopularCategories (${API_CONFIG.version}):`,
        error
      );
      throw error;
    }
  },

  // ✅ NUEVO: Servicio para obtener perfil del usuario con datos de facturación
  user: {
    /**
     * Obtener perfil completo del usuario con datos de facturación
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} - Datos del perfil del usuario
     */
    getProfile: async (token) => {
      if (!token) {
        throw new Error("Token requerido para obtener perfil");
      }

      try {
        // Usar la URL base de v2 directamente
        const url = `${API_CONFIG.V2_BASE_URL}/me/profile`;
        console.log(`👤 Fetching user profile (${API_CONFIG.version}):`, url);

        const response = await fetch(url, {
          method: "GET",
          headers: createAuthHeaders(token),
        });

        const responseData = await handleResponse(response);

        if (!responseData.success) {
          throw new Error(
            responseData.error?.message || "Error al obtener perfil"
          );
        }

        console.log(`✅ User profile fetched successfully:`, {
          userId: responseData.data?.id,
          hasBillingData: !!responseData.data?.billingData,
        });

        return responseData.data;
      } catch (error) {
        console.error(`❌ Error fetching user profile:`, error);
        throw error;
      }
    },
  },

  // ✅ NUEVO: Servicio específico para cotizaciones con manejo de autenticación
  quotes: {
    getQuotes: async (params = {}, token) => {
      if (!token) {
        throw new Error("Token requerido para acceder a cotizaciones");
      }

      try {
        const queryParams = buildQueryParams(params);
        const url = `${API_BASE_URL}/quotes?${queryParams}`;

        console.log(`🔍 Fetching quotes (${API_CONFIG.version}):`, url);

        const response = await fetch(url, {
          method: "GET",
          headers: createAuthHeaders(token),
        });

        return await handleResponse(response);
      } catch (error) {
        console.error(
          `❌ Error fetching quotes (${API_CONFIG.version}):`,
          error
        );
        throw error;
      }
    },

    updateQuoteStatus: async (quoteId, statusData, token) => {
      if (!token) {
        throw new Error("Token requerido para actualizar cotizaciones");
      }

      try {
        const response = await fetch(
          `${API_BASE_URL}/quotes/${quoteId}/status-priority`,
          {
            method: "PATCH",
            headers: createAuthHeaders(token),
            body: JSON.stringify(statusData),
          }
        );

        return await handleResponse(response);
      } catch (error) {
        console.error(
          `❌ Error updating quote status (${API_CONFIG.version}):`,
          error
        );
        throw error;
      }
    },

    /**
     * Descargar PDF de cotización
     * @param {number} quoteId - ID de la cotización (numérico)
     * @param {string} token - Token de autenticación
     * @returns {Promise<Blob>} - Blob del PDF para descargar
     */
    downloadPdf: async (quoteId, token) => {
      if (!token) {
        throw new Error("Token requerido para descargar PDF de cotización");
      }

      try {
        const url = `${API_BASE_URL}/quotes/${quoteId}/pdf`;
        console.log(`📄 Downloading quote PDF (${API_CONFIG.version}):`, url);

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error?.message ||
              `Error al descargar PDF: ${response.status}`
          );
        }

        // Obtener el blob del PDF
        const pdfBlob = await response.blob();

        console.log(`✅ PDF downloaded successfully (${API_CONFIG.version}):`, {
          quoteId,
          size: pdfBlob.size,
          type: pdfBlob.type,
        });

        return pdfBlob;
      } catch (error) {
        console.error(
          `❌ Error downloading quote PDF (${API_CONFIG.version}):`,
          error
        );
        throw error;
      }
    },

    /**
     * Descargar y guardar PDF de cotización
     * @param {number} quoteId - ID de la cotización (numérico)
     * @param {string} quoteReference - Referencia de la cotización (QUOTE-XXXX)
     * @param {string} token - Token de autenticación
     */
    downloadAndSavePdf: async (quoteId, quoteReference, token) => {
      try {
        const pdfBlob = await productsService.quotes.downloadPdf(
          quoteId,
          token
        );

        // Crear URL del blob
        const blobUrl = window.URL.createObjectURL(pdfBlob);

        // Crear elemento de descarga
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `cotizacion-${quoteReference || quoteId}.pdf`;

        // Añadir al DOM, hacer clic y remover
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Limpiar URL del blob
        window.URL.revokeObjectURL(blobUrl);

        console.log(
          `💾 PDF saved successfully: cotizacion-${
            quoteReference || quoteId
          }.pdf`
        );

        return true;
      } catch (error) {
        console.error(`❌ Error saving quote PDF:`, error);
        throw error;
      }
    },
  },
};

export default productsService;
